import React from 'react';
import { IoAdd, IoClose, IoTrashOutline, IoChatbubbleOutline } from 'react-icons/io5';
import { CText } from '../../../components/common';
import { Conversation, groupConversationsByDate } from '../../../api/chat';

interface ChatSidebarProps {
  conversations: Conversation[];
  currentConversationId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectConversation: (id: number) => void;
  onNewChat: () => void;
  onDeleteConversation: (id: number) => void;
  colors: any;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({
  conversations,
  currentConversationId,
  isOpen,
  onClose,
  onSelectConversation,
  onNewChat,
  onDeleteConversation,
  colors,
}) => {
  const groupedConversations = groupConversationsByDate(conversations);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:relative inset-y-0 left-0 z-50
          w-72 lg:w-64
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
        `}
        style={{
          backgroundColor: colors.inputBackground,
          borderRight: `1px solid ${colors.borderColor}`,
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4"
          style={{ borderBottom: `1px solid ${colors.borderColor}` }}
        >
          <CText type="B16" color={colors.textColor}>
            Chat History
          </CText>
          <div className="flex items-center gap-2">
            <button
              onClick={onNewChat}
              className="p-2 rounded-lg transition-colors hover:opacity-80"
              style={{ backgroundColor: colors.primary }}
              title="New Chat"
            >
              {React.createElement(IoAdd as any, { size: 20, color: colors.white })}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg lg:hidden"
              style={{ backgroundColor: colors.backgroundColor }}
            >
              {React.createElement(IoClose as any, { size: 20, color: colors.textColor })}
            </button>
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2">
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4">
              {React.createElement(IoChatbubbleOutline as any, {
                size: 48,
                color: colors.grayScale400,
              })}
              <CText type="M14" color={colors.grayScale400} style={{ marginTop: 12 }}>
                No conversations yet
              </CText>
              <CText type="r12" color={colors.grayScale500} style={{ marginTop: 4 }}>
                Start a new chat to begin
              </CText>
            </div>
          ) : (
            Object.entries(groupedConversations).map(([group, convs]) => (
              <div key={group} className="mb-4">
                <CText
                  type="r12"
                  color={colors.grayScale400}
                  style={{ padding: '8px 12px', textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  {group}
                </CText>
                {convs.map(conv => (
                  <div
                    key={conv.id}
                    className={`
                      flex items-center justify-between p-3 rounded-lg mb-1 cursor-pointer
                      transition-colors group
                    `}
                    style={{
                      backgroundColor:
                        currentConversationId === conv.id
                          ? colors.primary + '20'
                          : 'transparent',
                    }}
                    onClick={() => onSelectConversation(conv.id)}
                  >
                    <div className="flex-1 min-w-0 pr-2">
                      <CText
                        type="M14"
                        color={
                          currentConversationId === conv.id
                            ? colors.primary
                            : colors.textColor
                        }
                        style={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {conv.title || 'Untitled Conversation'}
                      </CText>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteConversation(conv.id);
                      }}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 p-1 rounded transition-opacity"
                      style={{ backgroundColor: colors.alertColor + '20' }}
                      title="Delete conversation"
                    >
                      {React.createElement(IoTrashOutline as any, {
                        size: 16,
                        color: colors.alertColor,
                      })}
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default ChatSidebar;
